import { reactive } from "vue";
import { INTL_LOCALES } from "./config.js";
import {
  detectInitialLocale,
  isSupportedLocale,
  storeLocale,
} from "./detect.js";
import {
  getByPath,
  interpolate,
  isTranslationLeaf,
  resolveLeaf,
} from "./localize.js";

export { localize } from "./localize.js";

const isDev = process.env.NODE_ENV !== "production";

const state = reactive({
  locale: detectInitialLocale(),
});

let messages = {};

const warnedKeys = new Set();
function warnOnce(key, message) {
  if (!isDev || warnedKeys.has(key)) return;
  warnedKeys.add(key);
  console.warn(`[i18n] ${message}`);
}

function applyDocumentLang(locale) {
  document.documentElement.setAttribute("lang", locale);
}

/** @returns {string} */
export function getLocale() {
  return state.locale;
}

/**
 * Ručně nastaví jazyk a uloží volbu uživatele.
 * @param {string} locale
 */
export function setLocale(locale) {
  if (!isSupportedLocale(locale)) {
    warnOnce(`locale:${locale}`, `Nepodporovaný jazyk "${locale}".`);
    return;
  }
  state.locale = locale;
  storeLocale(locale);
  applyDocumentLang(locale);
}

export function t(key, params) {
  const leaf = getByPath(messages, key);
  if (!isTranslationLeaf(leaf)) {
    warnOnce(`key:${key}`, `Chybí UI překlad pro klíč "${key}".`);
    return key;
  }
  return interpolate(String(resolveLeaf(leaf, state.locale)), params);
}

/**
 * @param {Date | string | number} date
 * @param {Intl.DateTimeFormatOptions} [options]
 * @returns {string}
 */
export function formatDate(date, options) {
  return new Intl.DateTimeFormat(INTL_LOCALES[state.locale], options).format(
    date instanceof Date ? date : new Date(date),
  );
}

export default {
  /**
   * @param {import("vue").App} app
   * @param {{ messages?: object }} [options]
   */
  install(app, options = {}) {
    messages = options.messages ?? {};

    const globals = app.config.globalProperties;
    globals.$t = t;
    globals.$setLocale = setLocale;
    globals.$formatDate = formatDate;
    Object.defineProperty(globals, "$locale", {
      get: getLocale,
      enumerable: true,
    });

    applyDocumentLang(state.locale);
  },
};
